import { useCallback, useMemo } from "react";
import { I18n } from "i18n-js";
import { useAtomValue } from "jotai";
import { currentLang, currentTranslations } from "../atoms/i18n";
import { formatDuration } from "date-fns";
import { frCH, de } from "date-fns/locale";
import { MissingBehavior } from "i18n-js/typings/typing";
import { formatCurrency, FormatCurrencyType, formatWorkingTimeClassicSpec } from "../utils";
import { Buildable } from "ts-essentials";
import { Contract, WorkingTimeClassicType } from "../__generated__/graphql";
import countries from "i18n-iso-countries";
// The country names must be registered wherever the search runs, not only from the app's entry.
import "../configs/countries-locales";
import type { SearchItemsFunction } from "../components/select/SelectChooser";

type LanguageLocale = {
    /** BCP-47 tag passed to Intl for date/time formatting. */
    locale: string;
    /** date-fns locale object, used for duration formatting. */
    dateFnsLocale: typeof frCH;
};

// Single source of truth mapping a UI language to its Swiss formatting locale.
// Formatting always stays Swiss (the "-CH" region); only the language varies.
const LANGUAGE_LOCALES_MAP: Record<string, LanguageLocale> = {
    fr: {
        locale: "fr-CH",
        dateFnsLocale: frCH,
    },
    de: {
        locale: "de-CH",
        dateFnsLocale: de,
    },
};

// Swiss-French is the fallback for any language without a Swiss locale mapping.
const DEFAULT_LANGUAGE_LOCALE = LANGUAGE_LOCALES_MAP.fr;

const getLanguageLocale = (lang: string): LanguageLocale =>
    LANGUAGE_LOCALES_MAP[lang] ?? DEFAULT_LANGUAGE_LOCALE;

/**
 * Returns the BCP-47 locale tag for the app's current language (the `currentLang`
 * atom, which follows the device default, the in-app language selector, and the
 * signed-in user's `lang`), e.g. "de" -> "de-CH". Falls back to the default
 * Swiss-French locale for any unsupported language.
 */
export const useUserLocaleTag = () => {
    const lang = useAtomValue(currentLang);
    return getLanguageLocale(lang).locale;
};

class I18nInternal {
    i18n: I18n;
    locale: string;

    constructor(i18n: I18n) {
        this.i18n = i18n;
        this.locale = i18n.locale;
    }

    translate(key: string, options?: object) {
        return this.i18n.translate(key, options);
    }

    t(key: string, options?: object) {
        return this.i18n.t(key, options);
    }

    /**
     * translate with an enum value parameter
     * enum MyEnum {
     *     key1 = "key1",
     *     key2 = "key2",
     * }
     * x = MyEnum.key1
     * i18n.tx("my.value", x, "title") -> "my.value.key1.title"
     * This is used to avoid dynamic keys in translation code to check for existing translations keys usage in code
     * @param prefix
     * @param type
     * @param suffix
     */
    tx<P extends string, T extends string, S extends string | undefined = undefined>(
        prefix: P,
        type: T,
        suffix: S,
    ) {
        return `${prefix}.${type}${suffix ? `.${suffix}` : ""}` as S extends string
            ? `${P}.${T}.${S}`
            : `${P}.${T}`;
    }
}

/**
 * Hook with Suspense support
 */
export const useI18n = () => {
    const lang = useAtomValue(currentLang);
    const translations = useAtomValue(currentTranslations);
    return useMemo(() => {
        return new I18nInternal(
            new I18n(translations, {
                locale: lang,
            }),
        );
    }, [lang, translations]);
};

/**
 * Formats amounts the Swiss way (point as decimal separator, apostrophe as
 * thousands separator). Deliberately independent from the UI language: a French
 * or German locale would render a comma, which is not how amounts are written in
 * Switzerland. See `app/utils/number-format.ts`.
 */
export const useCurrencyFormatter = () => {
    return useCallback((params: FormatCurrencyType) => formatCurrency(params), []);
};

export const useDurationFormatter = () => {
    const lang = useAtomValue(currentLang);
    const dateFnsLocale = getLanguageLocale(lang).dateFnsLocale;
    return useCallback(
        (durationInMinutes: number) => {
            const h = Math.floor(durationInMinutes / 60);
            const m = Math.round(durationInMinutes % 60);
            return formatDuration(
                { minutes: m, hours: h },
                {
                    zero: false,
                    locale: dateFnsLocale,
                    format: ["hours", "minutes"],
                },
            );
        },
        [dateFnsLocale],
    );
};

export const useDurationShortFormatter = () => {
    const i18n = useI18n();
    return useCallback(
        (durationInMinutes: number, dailyHours?: number) => {
            const h = Math.floor(durationInMinutes / 60);
            const m = Math.round(durationInMinutes % 60);
            let result = "";
            if (h > 0) {
                result += `${h}h`;
            }
            if (m > 0) {
                result += `${m.toString().padStart(2, "0")}m`;
            }
            if (dailyHours && h >= dailyHours) {
                const days = parseFloat((durationInMinutes / 60 / dailyHours).toFixed(1));
                const strDays = i18n.t("utils.working_time.classic.nb_days", { count: days });
                result += ` (${strDays})`;
            }
            return result;
        },
        [i18n],
    );
};

export const useNationalityFormatter = () => {
    const i18n = useI18n();
    return useCallback(
        (
            countryCode: string,
            gender?: "male" | "female" | "unknown" | null,
            missingBehavior?: MissingBehavior,
        ) => {
            const k = `nationalities.${countryCode.toUpperCase()}`;
            const base = i18n.t(k, { missingBehavior: missingBehavior });
            const [m, f] = base.split("|");
            if (gender == "female" && f) {
                return f;
            }
            if (gender == "male") {
                return m;
            }
            return base;
        },
        [i18n],
    );
};

export const useFormatContractWorkingTime = () => {
    const formatWorkingTimeClassicSpec = useFormatWorkingTimeClassicSpec();

    return useCallback(
        (contract?: Buildable<Contract> | null) => {
            if (contract?.workingTimeClassicSpec) {
                return formatWorkingTimeClassicSpec(contract.workingTimeClassicSpec);
            }

            return "";
        },
        [formatWorkingTimeClassicSpec],
    );
};

export const useFormatWorkingTimeClassicSpec = () => {
    const i18n = useI18n();

    return useCallback(
        (workingTimeSpec: Buildable<WorkingTimeClassicType>) =>
            formatWorkingTimeClassicSpec(workingTimeSpec, i18n),
        [i18n],
    );
};
/**
 * Searches every ISO country by its name in the app's language, Switzerland first. Unlike
 * `useAllCountryCodes`, it is not limited to the nationalities the app can phrase.
 */
export const useSearchCountries = (): SearchItemsFunction<string> => {
    const lang = useAtomValue(currentLang);
    const items = useMemo(
        () =>
            Object.entries(countries.getNames(lang))
                .map(([code, name]) => ({ value: code, label: name }))
                .sort((a, b) =>
                    a.value === "CH"
                        ? -1
                        : b.value === "CH"
                          ? 1
                          : a.label.localeCompare(b.label, lang),
                ),
        [lang],
    );
    return useCallback(
        (filter: string) => {
            const normalized = normalizeSearch(filter);
            return items.filter((item) => normalizeSearch(item.label).includes(normalized));
        },
        [items],
    );
};

const normalizeSearch = (text: string) =>
    text
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

export const useAllCountryCodes = (): Array<string> => {
    return useMemo(() => {
        return [
            "AL",
            "AD",
            "AT",
            "BY",
            "BE",
            "BA",
            "BG",
            "HR",
            "CY",
            "CZ",
            "DK",
            "EE",
            "FI",
            "FR",
            "DE",
            "GR",
            "HU",
            "IS",
            "IE",
            "IT",
            "XK",
            "LV",
            "LI",
            "LT",
            "LU",
            "MK",
            "MT",
            "MD",
            "MC",
            "ME",
            "NL",
            "NO",
            "PL",
            "PT",
            "RO",
            "RU",
            "SM",
            "RS",
            "SK",
            "SI",
            "ES",
            "SE",
            "CH",
            "UA",
            "GB",
        ];
    }, []);
};
